import React, { useState } from 'react';
import { applyProtectionLimits } from '../../services/protectBuild';
import { LABEL, NOTE } from './typeScale';
import {
  DATA_CLASSIFICATIONS, AUTONOMY_CHOICES, DPG_SCAN_LEVELS, LIMIT_DEFAULTS,
} from '../../data/protectionLimits';

/**
 * What it may never do, asked before it is put to work.
 *
 * <h3>Why the walk could not finish without this</h3>
 *
 * <p>The readiness gate asks whether any protection rules are in force
 * (`governance_materialized`). Nothing in this walk ever put any there:
 * `applyProtectionLimits`, which materializes them and then raises the trust
 * ceiling, had no caller outside its own tests. So the gate could never pass
 * from this surface and an agent built here could never go live.</p>
 *
 * <h3>Why none of the choices are decided here</h3>
 *
 * <p>The five questions, their ladders and their defaults come from
 * `data/protectionLimits`, which is asserted against the enforcement service's
 * own ranking. A cap is rank-expanded into an allowlist on the far side — a
 * classification capped at Internal allows Public and blocks Confidential — so
 * an order invented in this component would make the choice somebody makes
 * different from the rule the gateway enforces.</p>
 *
 * <h3>What it records</h3>
 *
 * <p>What the platform did, not what was asked for. The rules land first and the
 * ceiling second, and the half-applied case is real: limits in force, ceiling
 * unset, which leaves an agent protected that still will not pass its gate. The
 * service returns that in its own words and the step shows them rather than
 * claiming a success nobody got.</p>
 */
export default function ProtectionLimitsAsk({
  registrationId, agentId, policySkillId = null, onAnswer, busy = false,
}) {
  const [limits, setLimits] = useState(LIMIT_DEFAULTS);
  const [working, setWorking] = useState(false);
  const [problem, setProblem] = useState(null);

  const set = (key) => (e) => setLimits((p) => ({ ...p, [key]: e.target.value }));

  const apply = async () => {
    setWorking(true);
    setProblem(null);
    try {
      const res = await applyProtectionLimits(registrationId, agentId, limits, policySkillId);
      /* Two ways not to advance: nothing landed, or the rules landed and the
         ceiling did not. The second still has `ok: true` — reading only that
         would report an agent as ready that cannot pass its gate. */
      if (!res || res.ok === false || res.error) {
        setProblem(res?.error || 'The platform would not put these limits in force.');
        return;
      }
      onAnswer({ rules: res.rules, ceiling: res.ceiling, ...limits });
    } catch (e) {
      setProblem(e?.message || 'Could not put these limits in force.');
    } finally {
      setWorking(false);
    }
  };

  return (
    <div style={COL}>
      <div style={PANEL}>
        <Field id="pl-class" label="Data it may handle"
               hint="The most sensitive class it may touch. Anything less sensitive is allowed with it."
               value={limits.dataClassification} onChange={set('dataClassification')}
               options={DATA_CLASSIFICATIONS} />
        <Field id="pl-auto" label="How much it may do on its own"
               hint="Checked on every protected call, and it sets the trust ceiling the promotion gate reads."
               value={limits.maxAutonomyLevel} onChange={set('maxAutonomyLevel')}
               options={AUTONOMY_CHOICES} />
        <Field id="pl-dpg" label="Sensitive data it comes across"
               hint="What happens to it before the agent can use it."
               value={limits.dpgScanLevel} onChange={set('dpgScanLevel')}
               options={DPG_SCAN_LEVELS} />

        <div style={ROW}>
          <label htmlFor="pl-approvals" style={LABEL}>Approvals on each decision</label>
          <input id="pl-approvals" className="p7-input" type="number" min="0" max="5"
                 value={limits.requiredApprovals} onChange={set('requiredApprovals')}
                 style={{ width: '90px' }} />
        </div>
        <div style={ROW}>
          <label htmlFor="pl-rate" style={LABEL}>Calls a minute</label>
          <input id="pl-rate" className="p7-input" type="number" min="1" max="6000"
                 value={limits.rateLimitPerMinute} onChange={set('rateLimitPerMinute')}
                 style={{ width: '110px' }} />
        </div>
      </div>

      {problem ? (
        <p style={{ ...NOTE, color: 'var(--p7-deny-ink)' }}>{problem}</p>
      ) : null}

      <div>
        <button type="button" className="p7-btn p7-btn--primary"
                disabled={busy || working || !registrationId}
                onClick={apply}>
          {working ? 'Putting them in force…' : 'Put these in force'}
        </button>
      </div>
    </div>
  );
}

function Field({ id, label, hint, value, onChange, options }) {
  return (
    <div style={{ marginBottom: '11px' }}>
      <label htmlFor={id} style={LABEL}>{label}</label>
      <select id={id} className="p7-input" value={value} onChange={onChange}
              style={{ display: 'block', marginTop: '4px', width: '100%', maxWidth: '420px' }}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <p style={{ ...NOTE, marginTop: '4px' }}>{hint}</p>
    </div>
  );
}

const COL = { display: 'flex', flexDirection: 'column', gap: 'var(--p7-space-3)' };
const ROW = { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '9px' };
const PANEL = {
  border: '1px solid var(--p7-line)', borderRadius: 'var(--p7-radius-control)',
  padding: '12px 13px', background: 'var(--p7-surface-sunken)',
};
